import React from 'react';
import { Shield, CheckCircle2, Edit3, Clock } from 'lucide-react';
import { BracketMatch, Config } from '../../types';
import { formatPairName, formatPlayerName } from '../../lib/utils';

interface MatchNodeProps {
  key?: any;
  id?: string;
  match: BracketMatch;
  roundName: string;
  getTeamInfo: (teamId: string, fallbackName?: string) => { id: string | null; name: string; photoUrl?: string };
  isFinal?: boolean;
  isAdmin?: boolean;
  onEdit?: () => void;
  onDropTeam?: (teamId: string, teamName: string, slot: 1 | 2, sourceInfo?: any) => void;
  onDropGlobal?: (bIdx: number, mIdx: number, teamId: string, teamName: string, slot: 1 | 2, sourceInfo?: any) => void;
  onRemoveTeam?: (slot: 1 | 2) => void;
  onPublishMatch?: () => void;
  config: Config;
  bIdx?: number;
  mIdx?: number;
  globalMatches?: any[];
  isExpressMode?: boolean;
  onEditDateTime?: () => void;
}

export function MatchNode({
  id,
  match,
  roundName,
  getTeamInfo,
  isFinal = false,
  isAdmin = false,
  onEdit,
  onDropTeam,
  onDropGlobal,
  onRemoveTeam,
  onPublishMatch,
  config,
  bIdx,
  mIdx,
  globalMatches = [],
  isExpressMode = false,
  onEditDateTime,
}: MatchNodeProps) {
  if (!match) return null;
  const [showPublishedBadge, setShowPublishedBadge] = React.useState(false);
  const prevPublished = React.useRef(match.isPublished);

  React.useEffect(() => {
    // Only show the badge if it transitioned from false to true in the current session
    if (match.isPublished && !prevPublished.current) {
      setShowPublishedBadge(true);
      const timer = setTimeout(() => setShowPublishedBadge(false), 1200);
      return () => clearTimeout(timer);
    }
    prevPublished.current = match.isPublished;
  }, [match.isPublished]);

  const t1 = getTeamInfo(match.team1Id, match.t1);
  const t2 = getTeamInfo(match.team2Id, match.t2);
  const isW1 = match.winnerId === match.team1Id && match.team1Id;
  const isW2 = match.winnerId === match.team2Id && match.team2Id;

  // Check if match is already in global results
  const isBye = match.team1Id === 'BYE' || match.team2Id === 'BYE';
  const matchIdInCollection = `bracket_${id?.split('-')[1]}_${bIdx}_${mIdx}`;
  const isAlreadyInResults = globalMatches.some(m => m.id === matchIdInCollection);

  const parseScore = (s: string) => {
    if (!s) return { t1: '', t2: '' };
    const parts = s.includes('-') ? s.split('-') : s.split('/');
    if (parts.length === 2) return { t1: parts[0].trim(), t2: parts[1].trim() };
    return { t1: s, t2: '' };
  };

  const set1 = parseScore(match.s1);
  const set2 = parseScore(match.s2);
  const set3 = parseScore(match.s3);

  const hasParsedScores = !!(set1.t1 || set2.t1 || set3.t1);
  const oldScoreDisplay = !hasParsedScores && !isBye ? (match.score || '') : '';
  const hasResult = !!match.winnerId || !!match.score || hasParsedScores;

  const getStyle = (isWinner: any) => {
    if (!isWinner) return { backgroundColor: 'transparent', border: '1px solid transparent' };
    return { backgroundColor: `${config?.primaryColor || '#eab308'}33`, border: `1px solid ${config?.primaryColor || '#eab308'}66` };
  };

  const getTextColor = (isWinner: any) => {
    if (isWinner && isFinal) return '#fbbf24'; // Gold
    return isWinner ? (config?.primaryColor || '#eab308') : 'inherit';
  };

  const getSurnames = (teamName: string) => {
    if (!teamName) return ['', ''];
    const originalSeparator = teamName.includes(' & ') ? ' & ' : teamName.includes(' / ') ? ' / ' : null;
    if (!originalSeparator) return [formatPlayerName(teamName, 'minimal'), ''];
    const players = teamName.split(originalSeparator);
    return [
      formatPlayerName(players[0] || '', 'minimal'),
      formatPlayerName(players[1] || '', 'minimal')
    ];
  };

  const [t1p1, t1p2] = getSurnames(t1.name);
  const [t2p1, t2p2] = getSurnames(t2.name);

  const handleDragOver = (e: React.DragEvent) => {
    if (!isAdmin || hasResult) return;
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, slot: 1 | 2) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverSlot(null);
    if (!isAdmin || !onDropTeam || hasResult) return;

    let teamId = '';
    let teamName = '';
    let sourceInfo = undefined;

    // Try robust JSON first
    const plainData = e.dataTransfer.getData('text/plain');
    if (plainData) {
      try {
        const parsed = JSON.parse(plainData);
        teamId = parsed.teamId;
        teamName = parsed.teamName;
        sourceInfo = parsed.sourceInfo;
      } catch (err) {
        console.warn('[MatchNode] plain data was not JSON:', plainData);
      }
    }

    // Fallback exactly to the old way for any lingering interactions
    if (!teamId) {
      teamId = e.dataTransfer.getData('teamId');
      teamName = e.dataTransfer.getData('teamName');
      const sourceRaw = e.dataTransfer.getData('sourceInfo');
      if (sourceRaw) {
        try {
          sourceInfo = JSON.parse(sourceRaw);
        } catch (e) { }
      }
    }

    console.log('[MatchNode] Parsed drop data:', { teamId, teamName, sourceInfo });

    if (teamId) {
      console.log('[MatchNode] Invoking onDropTeam with', { teamId, teamName, slot, sourceInfo });
      onDropTeam(teamId, teamName, slot, sourceInfo);
    } else {
      console.warn('[MatchNode] No teamId found in dataTransfer');
    }
  };

  const [dragOverSlot, setDragOverSlot] = React.useState<1 | 2 | null>(null);
  const [draggingSlot, setDraggingSlot] = React.useState<1 | 2 | null>(null);

  // Touch Support (for Mobile)
  const [isTouchDragging, setIsTouchDragging] = React.useState(false);

  const handleTouchStart = (e: React.TouchEvent, slot: 1 | 2, teamId: string | null, teamName: string) => {
    if (!isAdmin || !teamId) return;

    const initialTouch = e.touches[0];
    const startX = initialTouch.clientX;
    const startY = initialTouch.clientY;

    let localIsDragging = false;
    let dragAuthorized = false; // Will be true after 300ms hold
    let ghost: HTMLDivElement | null = null;
    const sourceInfo = { type: 'bracket', bIdx, mIdx, slot };

    const longPressTimer = setTimeout(() => {
      dragAuthorized = true;
    }, 300);

    const onMove = (moveEv: TouchEvent) => {
      const t = moveEv.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // If we haven't authorized drag (300ms) but user moved significantly, it's a scroll
      if (!dragAuthorized && distance > 10) {
        clearTimeout(longPressTimer);
        return;
      }

      // If we are dragging or authorized to start
      if (dragAuthorized && !localIsDragging && distance > 5) {
        localIsDragging = true;
        setIsTouchDragging(true);
        setDraggingSlot(slot);

        ghost = document.createElement('div');
        ghost.id = 'drag-ghost-mobile';
        ghost.innerHTML = `<div class="flex items-center theme-primary-bg text-black px-4 py-2 rounded-full text-[12px] font-black shadow-lg border-2 border-white pointer-events-none whitespace-nowrap"><span class="mr-2 opacity-50 italic">ASIGNANDO...</span>${teamName}</div>`;
        ghost.style.position = 'fixed';
        ghost.style.zIndex = '999999';
        ghost.style.pointerEvents = 'none';
        ghost.style.transform = 'translate(-50%, -150%)';
        ghost.style.left = `${t.clientX}px`;
        ghost.style.top = `${t.clientY}px`;
        document.body.appendChild(ghost);
      }

      // If we confirmed dragging:
      if (localIsDragging) {
        if (moveEv.cancelable) moveEv.preventDefault();

        if (ghost) {
          ghost.style.left = `${t.clientX}px`;
          ghost.style.top = `${t.clientY}px`;
          ghost.style.visibility = 'hidden';
        }

        const elUnderPoint = document.elementFromPoint(t.clientX, t.clientY);
        if (ghost) ghost.style.visibility = 'visible';

        const found = elUnderPoint?.closest('[data-drop-slot]');
        document.querySelectorAll('.touch-drop-target').forEach(el => el.classList.remove('touch-drop-target', 'ring-4', 'ring-theme-primary', 'bg-theme-primary/20', 'z-50'));
        if (found) found.classList.add('touch-drop-target', 'ring-4', 'ring-theme-primary', 'bg-theme-primary/20', 'z-50');
      }
    };

    const onEnd = () => {
      clearTimeout(longPressTimer);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);

      if (localIsDragging) {
        setIsTouchDragging(false);
        setDraggingSlot(null);

        const finalTarget = document.querySelector('.touch-drop-target');

        if (finalTarget) {
          const targetSlot = parseInt(finalTarget.getAttribute('data-drop-slot') || '1') as 1 | 2;
          const targetBIdx = parseInt(finalTarget.getAttribute('data-bidx') || '0');
          const targetMIdx = parseInt(finalTarget.getAttribute('data-midx') || '0');

          if (onDropGlobal) onDropGlobal(targetBIdx, targetMIdx, teamId, teamName, targetSlot, sourceInfo);
          else if (onDropTeam) onDropTeam(teamId, teamName, targetSlot, sourceInfo);
        }

        document.querySelectorAll('.touch-drop-target').forEach(el => el.classList.remove('touch-drop-target', 'ring-4', 'ring-theme-primary', 'bg-theme-primary/20', 'z-50'));
        if (ghost && ghost.parentNode) ghost.parentNode.removeChild(ghost);
      }
    };

    // Attach listeners immediately to window, without timeout
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd, { passive: false });
    window.addEventListener('touchcancel', onEnd, { passive: false });
  };

  const canPublish = isAdmin && match.team1Id && match.team2Id && (!match.winnerId || isBye) && !isAlreadyInResults;

  return (
    <div
      id={id}
      style={{ touchAction: isAdmin ? 'none' : 'auto' }}
      className={`relative bg-white/[0.02] backdrop-blur-md rounded-lg border p-1 flex flex-col justify-center shadow-2xl transition-all duration-300 group ${isAdmin ? 'cursor-default' : 'hover:bg-white/[0.04] hover:border-white/20'} ${isFinal ? 'theme-accent-border shadow-[0_0_15px_rgba(var(--accent-rgb),0.15)] scale-[1.02]' : 'border-white/10'}`}
    >
      {isAdmin && showPublishedBadge && (
        <div className="absolute inset-x-1 -top-4 z-[30] flex items-center justify-center p-0.5 theme-primary-bg rounded shadow-lg">
          <span className="text-black font-black uppercase text-[6px] tracking-widest flex items-center leading-none">
            <CheckCircle2 size={10} className="mr-1" /> Publicado
          </span>
        </div>
      )}

      {isAdmin && (
        <button
          onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
          className="absolute -top-2 -right-2 theme-primary-bg rounded-full p-1 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity shadow-md text-black z-20 hover:scale-110"
        >
          <Edit3 size={10} />
        </button>
      )}

      {roundName && (
        <div className="text-[7px] md:text-[8px] font-black theme-primary-text uppercase tracking-widest text-center mb-1 opacity-80">
          {roundName}
        </div>
      )}

      {isExpressMode && (match.date || match.time || isAdmin) && (
        <div className="flex justify-center mb-1.5 relative z-10">
          <div 
            onClick={isAdmin ? (e) => { e.stopPropagation(); onEditDateTime?.(); } : undefined}
            className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[6px] md:text-[7px] font-black uppercase tracking-wider ${isAdmin ? 'cursor-pointer hover:scale-105 transition-transform active:scale-95' : ''} ${(match.date || match.time) ? 'bg-[#25D366]/10 border border-[#25D366]/20 text-[#25D366]' : 'bg-white/5 border border-white/10 text-white/40 hover:text-white/80 hover:bg-white/10'}`}
          >
            <Clock size={8} />
            {match.date || match.time ? (
              <span>{match.date ? match.date.split('-').reverse().slice(0,2).join('/') : ''} {match.time || ''}{match.time && 'hs'}</span>
            ) : (
              <span>Fijar Horario</span>
            )}
          </div>
        </div>
      )}

      <div className="space-y-[1px]">        <div
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (isAdmin && !hasResult) setDragOverSlot(1); }}
        onDragLeave={() => setDragOverSlot(null)}
        onDrop={(e) => handleDrop(e, 1)}
        onTouchStart={(e) => { if (!hasResult) handleTouchStart(e, 1, match.team1Id, t1.name); }}
        onContextMenu={(e) => e.preventDefault()}
        data-drop-slot="1"
        data-bidx={bIdx}
        data-midx={mIdx}
        draggable={isAdmin && !!match.team1Id && !hasResult}
        onDragStart={(e) => {
          if (isAdmin && match.team1Id && !hasResult) {
            e.stopPropagation();
            setDraggingSlot(1);
            const payload = {
              teamId: match.team1Id,
              teamName: t1.name,
              sourceInfo: { type: 'bracket', bIdx, mIdx, slot: 1 }
            };
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', JSON.stringify(payload));
            // Fallbacks
            e.dataTransfer.setData('teamId', match.team1Id);
            e.dataTransfer.setData('teamName', t1.name);
            e.dataTransfer.setData('sourceInfo', JSON.stringify(payload.sourceInfo));
          }
        }}
        onDragEnd={() => setDraggingSlot(null)}
        className={`relative flex items-center justify-between px-1.5 py-1 rounded-[4px] transition-all duration-200 ${draggingSlot === 1 ? 'opacity-30 scale-95 theme-primary-border border' : ''} ${dragOverSlot === 1 || (isTouchDragging && draggingSlot === 1) ? 'ring-2 ring-theme-primary bg-theme-primary/10 z-50' : ''} ${!isAdmin || hasResult ? '' : 'cursor-grab active:cursor-grabbing hover:bg-white/5 border border-transparent select-none'} ${isAdmin && !match.team1Id ? 'bg-black/20' : ''}`}
        style={getStyle(isW1)}
      >
        <style dangerouslySetInnerHTML={{
          __html: `
            @media (pointer: coarse) {
              div[draggable="true"] {
                -webkit-touch-callout: none;
                -webkit-user-drag: none;
                user-select: none;
              }
            }
          `}} />
        <div className="flex items-center flex-1 min-w-0">
          {isW1 && <div className="w-0.5 h-3 theme-primary-bg mr-1.5 rounded-full shadow-[0_0_5px_rgba(var(--glow-rgb),0.5)]"></div>}
          {!isW1 && <div className="w-0.5 h-3 bg-white/10 mr-1.5 rounded-full"></div>}
          <div
            className={`flex flex-col ${isFinal && isW1 ? 'drop-shadow-[0_0_8px_rgba(251,191,36,0.8)]' : ''} ${isW1 ? 'font-black opacity-100' : 'font-medium opacity-70 group-hover:opacity-100'} ${isAdmin && !match.team1Id ? 'italic opacity-40' : ''} leading-[1.1] break-words`}
            style={{ color: getTextColor(isW1) }}
          >
            <span className={isFinal && isW1 ? 'text-[9px] md:text-[10px]' : 'text-[7px] md:text-[8px]'}>
              {t1p1}
            </span>
            {t1p2 && (
              <span className={isFinal && isW1 ? 'text-[9px] md:text-[10px]' : 'text-[7px] md:text-[8px]'}>
                {t1p2}
              </span>
            )}
          </div>
          {isBye && match.team1Id !== 'BYE' && (
            <div className="ml-auto flex items-center mr-1 shrink-0">
              <span className="text-[5px] md:text-[6px] font-black uppercase tracking-[0.2em] bg-theme-primary/20 text-theme-primary px-1.5 py-0.5 rounded border border-theme-primary/30 whitespace-nowrap">
                Pase Directo
              </span>
            </div>
          )}
        </div>

        {/* Team 1 Scores */}
        <div className="flex gap-[1.5px] ml-1.5 text-[7.5px] md:text-[8.5px] font-black text-white">
          {set1.t1 && <span className="w-[12px] md:w-[14px] h-[12px] md:h-[14px] flex items-center justify-center bg-black/60 rounded-[2px] border border-white/5">{set1.t1}</span>}
          {set2.t1 && <span className="w-[12px] md:w-[14px] h-[12px] md:h-[14px] flex items-center justify-center bg-black/60 rounded-[2px] border border-white/5">{set2.t1}</span>}
          {set3.t1 && <span className="w-[12px] md:w-[14px] h-[12px] md:h-[14px] flex items-center justify-center bg-black/60 rounded-[2px] border border-white/5">{set3.t1}</span>}
        </div>

        {isAdmin && match.team1Id && !hasResult && (
          <button
            onClick={(e) => { e.stopPropagation(); onRemoveTeam?.(1); }}
            className="ml-1 opacity-0 group-hover:opacity-60 hover:!opacity-100 text-red-400 p-0.5 transition-opacity"
          >
            <span className="text-[7px] font-black">X</span>
          </button>
        )}
      </div>

        <div className="h-px w-full bg-white/5 my-0.5"></div>

        <div
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (isAdmin && !hasResult) setDragOverSlot(2); }}
          onDragLeave={() => setDragOverSlot(null)}
          onDrop={(e) => handleDrop(e, 2)}
          onTouchStart={(e) => { if (!hasResult) handleTouchStart(e, 2, match.team2Id, t2.name); }}
          onContextMenu={(e) => e.preventDefault()}
          data-drop-slot="2"
          data-bidx={bIdx}
          data-midx={mIdx}
          draggable={isAdmin && !!match.team2Id && !hasResult}
          onDragStart={(e) => {
            if (isAdmin && match.team2Id && !hasResult) {
              e.stopPropagation();
              setDraggingSlot(2);
              const payload = {
                teamId: match.team2Id,
                teamName: t2.name,
                sourceInfo: { type: 'bracket', bIdx, mIdx, slot: 2 }
              };
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', JSON.stringify(payload));
              // Fallbacks
              e.dataTransfer.setData('teamId', match.team2Id);
              e.dataTransfer.setData('teamName', t2.name);
              e.dataTransfer.setData('sourceInfo', JSON.stringify(payload.sourceInfo));
            }
          }}
          onDragEnd={() => setDraggingSlot(null)}
          className={`relative flex items-center justify-between px-1.5 py-1 rounded-[4px] transition-all duration-200 ${draggingSlot === 2 ? 'opacity-30 scale-95 theme-primary-border border' : ''} ${dragOverSlot === 2 || (isTouchDragging && draggingSlot === 2) ? 'ring-2 ring-theme-primary bg-theme-primary/10 z-50' : ''} ${!isAdmin || hasResult ? '' : 'cursor-grab active:cursor-grabbing hover:bg-white/5 border border-transparent select-none'} ${isAdmin && !match.team2Id ? 'bg-black/20' : ''}`}
          style={getStyle(isW2)}
        >
          <div className="flex items-center flex-1 min-w-0">
            {isW2 && <div className="w-0.5 h-3 theme-primary-bg mr-1.5 rounded-full shadow-[0_0_5px_rgba(var(--glow-rgb),0.5)]"></div>}
            {!isW2 && <div className="w-0.5 h-3 bg-white/10 mr-1.5 rounded-full"></div>}
            <div
              className={`flex flex-col ${isFinal && isW2 ? 'drop-shadow-[0_0_8px_rgba(251,191,36,0.8)]' : ''} ${isW2 ? 'font-black opacity-100' : 'font-medium opacity-70 group-hover:opacity-100'} ${isAdmin && !match.team2Id ? 'italic opacity-40' : ''} leading-[1.1] break-words`}
              style={{ color: getTextColor(isW2) }}
            >
              <span className={isFinal && isW2 ? 'text-[9px] md:text-[10px]' : 'text-[7px] md:text-[8px]'}>
                {t2p1}
              </span>
              {t2p2 && (
                <span className={isFinal && isW2 ? 'text-[9px] md:text-[10px]' : 'text-[7px] md:text-[8px]'}>
                  {t2p2}
                </span>
              )}
            </div>
            {isBye && match.team2Id !== 'BYE' && (
              <div className="ml-auto flex items-center mr-1 shrink-0">
                <span className="text-[5px] md:text-[6px] font-black uppercase tracking-[0.2em] bg-theme-primary/20 text-theme-primary px-1.5 py-0.5 rounded border border-theme-primary/30 whitespace-nowrap">
                  Pase Directo
                </span>
              </div>
            )}
          </div>

          {/* Team 2 Scores */}
          <div className="flex gap-[1.5px] ml-1.5 text-[7.5px] md:text-[8.5px] font-black text-white">
            {set1.t2 && <span className="w-[12px] md:w-[14px] h-[12px] md:h-[14px] flex items-center justify-center bg-black/60 rounded-[2px] border border-white/5">{set1.t2}</span>}
            {set2.t2 && <span className="w-[12px] md:w-[14px] h-[12px] md:h-[14px] flex items-center justify-center bg-black/60 rounded-[2px] border border-white/5">{set2.t2}</span>}
            {set3.t2 && <span className="w-[12px] md:w-[14px] h-[12px] md:h-[14px] flex items-center justify-center bg-black/60 rounded-[2px] border border-white/5">{set3.t2}</span>}
          </div>

          {isAdmin && match.team2Id && !hasResult && (
            <button
              onClick={(e) => { e.stopPropagation(); onRemoveTeam?.(2); }}
              className="ml-1 opacity-0 group-hover:opacity-60 hover:!opacity-100 text-red-400 p-0.5 transition-opacity"
            >
              <span className="text-[7px] font-black">X</span>
            </button>
          )}
        </div>
      </div>

      {canPublish && !isExpressMode && (
        <button
          onClick={(e) => { e.stopPropagation(); onPublishMatch?.(); }}
          className="mt-2 py-1 px-2 theme-primary-bg text-black text-[9px] font-black rounded uppercase hover:opacity-80 transition-opacity"
        >
          Publicar Partido
        </button>
      )}

      {isAdmin && isAlreadyInResults && !showPublishedBadge && !isExpressMode && (
        <div className="mt-2 py-1 px-2 theme-primary-bg/10 theme-primary-text text-[8px] font-black rounded uppercase text-center border theme-primary-border/20 opacity-60">
          Publicado
        </div>
      )}

      {oldScoreDisplay && (
        <div className="mt-1 flex justify-center">
          <div className="bg-black/40 rounded px-2 py-0.5 border border-white/5 inline-flex items-center shadow-inner">
            <span className={`text-[8px] md:text-[9px] font-black tracking-widest whitespace-nowrap ${isFinal ? 'theme-accent-text' : 'text-white/80'}`}>
              {oldScoreDisplay}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
